import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// ─── Toast Context ────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

const ICONS = {
  success: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  info: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const STYLES = {
  success: {
    container: 'bg-[#0a1a0a] border-emerald-500/30',
    icon: 'text-emerald-400 bg-emerald-500/10',
    bar: 'bg-emerald-500',
    text: 'text-white',
  },
  error: {
    container: 'bg-[#1a0a0a] border-red-500/30',
    icon: 'text-red-400 bg-red-500/10',
    bar: 'bg-red-500',
    text: 'text-white',
  },
  warning: {
    container: 'bg-[#1a160a] border-amber-500/30',
    icon: 'text-amber-400 bg-amber-500/10',
    bar: 'bg-amber-500',
    text: 'text-white',
  },
  info: {
    container: 'bg-[#0a0f1a] border-sky-500/30',
    icon: 'text-sky-400 bg-sky-500/10',
    bar: 'bg-sky-500',
    text: 'text-white',
  },
};

// ─── Single Toast Item ────────────────────────────────────────────────────────
const ToastItem = ({ toast, onRemove }) => {
  const style = STYLES[toast.type] || STYLES.info;

  return (
    <div
      className={`relative flex items-start gap-3 w-full max-w-sm px-4 py-3.5 rounded-2xl border backdrop-blur-xl shadow-2xl overflow-hidden
        ${style.container}
        animate-in slide-in-from-right-4 fade-in duration-300`}
    >
      {/* Progress bar */}
      <div
        className={`absolute bottom-0 left-0 h-[2px] ${style.bar} opacity-60`}
        style={{
          animation: `toast-shrink ${toast.duration}ms linear forwards`,
        }}
      />

      {/* Icon */}
      <div className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${style.icon}`}>
        {ICONS[toast.type]}
      </div>

      {/* Message */}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-[11px] font-black uppercase tracking-widest text-white mb-0.5">
            {toast.title}
          </p>
        )}
        <p className={`text-xs font-medium leading-relaxed ${style.text} opacity-80`}>
          {toast.message}
        </p>
      </div>

      {/* Close */}
      <button
        onClick={() => onRemove(toast.id)}
        className="text-white/30 hover:text-white/80 transition-colors shrink-0 mt-0.5 text-sm font-bold leading-none"
      >
        ✕
      </button>
    </div>
  );
};

// ─── Toast Container ──────────────────────────────────────────────────────────
const ToastContainer = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <>
      {/* CSS keyframe for the progress bar */}
      <style>{`
        @keyframes toast-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>

      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2.5 items-end pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto w-full max-w-sm">
            <ToastItem toast={toast} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </>
  );
};

// ─── Toast Provider ───────────────────────────────────────────────────────────
// Wrap your app (or just Portal) in this provider.
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', options = {}) => {
    const id = ++counterRef.current;
    const duration = options.duration || 4000;

    const toast = {
      id,
      message,
      type,
      title: options.title || null,
      duration,
    };

    setToasts(prev => [...prev.slice(-4), toast]); // max 5 toasts

    // Auto-remove after duration
    setTimeout(() => removeToast(id), duration + 300);
  }, [removeToast]);

  // Convenience methods
  const toast = {
    success: (message, options) => addToast(message, 'success', options),
    error: (message, options) => addToast(message, 'error', options),
    warning: (message, options) => addToast(message, 'warning', options),
    info: (message, options) => addToast(message, 'info', options),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// ─── useToast hook ────────────────────────────────────────────────────────────
// Usage inside any component wrapped in ToastProvider:
//   const toast = useToast();
//   toast.success("Settings saved!");
//   toast.error("Something went wrong.");
//   toast.warning("Check your input.");
//   toast.info("Loading data...");
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};

export default ToastProvider;